import React from 'react';
import { CURRENCIES } from '../../constants';

interface PlanFormFieldsProps {
  tierName: string;
  onTierNameChange: (val: string) => void;
  planId: string;
  onPlanIdChange: (val: string) => void;
  isEditing: boolean;
  price: number | string;
  onPriceChange: (val: string) => void;
  currency: string;
  onCurrencyChange: (val: string) => void;
}

const PlanFormFields: React.FC<PlanFormFieldsProps> = ({
  tierName,
  onTierNameChange,
  planId,
  onPlanIdChange,
  isEditing,
  price,
  onPriceChange,
  currency,
  onCurrencyChange,
}) => {
  const currencySymbol = CURRENCIES.find(c => c.code === currency)?.symbol || '₹';

  return (
    <div className="space-y-4">
      {/* 1. Name & 2. Plan ID */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Plan Name <span className="text-red-500">*</span>
          </label>
          <input
            required
            type="text"
            placeholder="e.g. Starter, Pro, Enterprise"
            value={tierName}
            onChange={e => onTierNameChange(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Plan ID <span className="text-red-500">*</span>
          </label>
          <input
            required
            type="text"
            placeholder="e.g. plan_starter, pro_tier"
            value={planId}
            disabled={isEditing}
            onChange={e => onPlanIdChange(e.target.value)}
            className="w-full px-4 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl font-mono text-xs text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 disabled:opacity-60"
          />
        </div>
      </div>

      {/* 3. Price & Currency */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-1.5">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Currency
          </label>
          <select
            value={currency}
            onChange={e => onCurrencyChange(e.target.value)}
            className="w-full px-3 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
          >
            {CURRENCIES.map(c => (
              <option key={c.code} value={c.code}>
                {c.code} ({c.symbol})
              </option>
            ))}
          </select>
        </div>

        <div className="sm:col-span-2 space-y-1.5">
          <label className="text-[11px] font-bold text-slate-700 dark:text-slate-200 uppercase tracking-wider">
            Price (Per Month) <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <span className="absolute left-4 top-2.5 text-slate-400 font-bold">{currencySymbol}</span>
            <input
              required
              type="number"
              min="0"
              step="any"
              placeholder="0"
              value={price}
              onChange={e => onPriceChange(e.target.value)}
              className="w-full pl-9 pr-16 py-2.5 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl font-bold text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span className="absolute right-4 top-2.5 text-xs text-slate-400 font-medium">/ month</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanFormFields;
